import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { FileService } from "@services/file.service";
import { ToastService } from "angular-toastify";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export type CadStandardView = "front" | "back" | "top" | "bottom" | "left" | "right" | "iso";
export type CadMeasureType = "" | "distance" | "angle";
export type CadSectionAxis = "x" | "y" | "z";

interface MaterialDensity { density: number; name: string; } // density in g/cm³
interface CadPart { mesh: any; name: string; surfaceAreaMm2: number; visible: boolean; volumeMm3: number; }
interface OcctMeshBuildResult { edges: any[]; meshes: any[]; parts: CadPart[]; }

@Component({
	moduleId: module.id,
	selector: "cad-viewer",
	styleUrls: ["cadViewer.css"],
	templateUrl: "cadViewer.html"
})
export class CadViewerComponent implements OnDestroy {
	@Output() compareFileRequested = new EventEmitter<void>();
	@Input() embedded = false;
	@Input() screenshotName = "cad-3d";
	@Input() title = "Inspect Part - 3D Viewer";

	@ViewChild("viewerContainer", { static: false }) viewerContainer!: ElementRef<HTMLDivElement>;

	materials: MaterialDensity[] = [
		{ density: 7.85, name: "Mild Steel" },
		{ density: 7.93, name: "Stainless Steel" },
		{ density: 2.70, name: "Aluminium" },
		{ density: 8.50, name: "Brass" },
		{ density: 8.96, name: "Copper" },
		{ density: 4.51, name: "Titanium" },
		{ density: 1.04, name: "ABS" },
		{ density: 0.91, name: "PP" },
		{ density: 1.14, name: "Nylon" }
	];
	selectedMaterialName = "";
	stpAutoRotate = false;
	stpBackgroundIndex = 0;
	stpBoundingBoxHelper: any = null;
	stpBoundingBoxVisible = false;
	stpCamera: any;
	stpCenter: any = null;
	stpClippingPlane: any = null;
	stpColourMode = 0;
	stpCompareMode = false;
	stpCompareOpacity = 0.6;
	stpControls: any;
	stpDimensions = "";
	stpDims: any = null;
	stpEdgeLines: any[] = [];
	stpEdgeOverlay = false;
	stpExploded = false;
	stpFileAColour = 0x4488cc;
	stpFileAMeshes: any[] = [];
	stpFileAVisible = true;
	stpFileBColour = 0xff8c42;
	stpFileBEdges: any[] = [];
	stpFileBMeshes: any[] = [];
	stpFileBParts: CadPart[] = [];
	stpFileBVisible = true;
	stpGizmoCamera: any = null;
	stpGizmoContainer: HTMLDivElement | null = null;
	stpGizmoRenderer: any = null;
	stpGizmoScene: any = null;
	stpInitialCameraPos: any;
	stpInitialTarget: any;
	stpIsOrthographic = false;
	stpMass = "";
	stpMeasureFirstNormal: any = null;
	stpMeasureMarkers: any[] = [];
	stpMeasurePoint1: any = null;
	stpMeasureResult = "";
	stpMeasureType: CadMeasureType = "";
	stpMeshCenters: any[] = [];
	stpMeshes: any[] = [];
	stpOriginalColours: number[] = [];
	stpPartTreeVisible = false;
	stpParts: CadPart[] = [];
	stpRenderer: any;
	stpScene: any;
	stpSectionAxis: CadSectionAxis = "x";
	stpSectionInverted = false;
	stpSectionPosition = 50; // 0-100 (percent of axis extent)
	stpSectionVisible = false;
	stpSize = 0;
	stpSurfaceArea = "";
	stpVolume = "";
	stpVolumeMm3 = 0;
	stpWireframe = false;
	visible = false;

	constructor(private fileService: FileService, private toastService: ToastService) {}

	applyColourMode() {
		// 0 = Grey (default), 1 = Original from STP, 2 = Blue Solid, 3 = Blue Transparent.
		// File A meshes only — in compare mode, file A keeps its compare colour and file B keeps its own.
		if (this.stpCompareMode) return;
		this.stpMeshes.forEach((mesh: any, i: number) => {
			if (this.stpColourMode === 0) {
				mesh.material.color.setHex(0xa0a0a0);
				mesh.material.opacity = 1;
				mesh.material.transparent = false;
			} else if (this.stpColourMode === 1) {
				mesh.material.color.setHex(this.stpOriginalColours[i]);
				mesh.material.opacity = 1;
				mesh.material.transparent = false;
			} else if (this.stpColourMode === 2) {
				mesh.material.color.setHex(0x4488cc);
				mesh.material.opacity = 1;
				mesh.material.transparent = false;
			} else {
				mesh.material.color.setHex(0x4488cc);
				mesh.material.opacity = 0.3;
				mesh.material.transparent = true;
			}
		});
	}

	applyCompareColours() {
		// File A → blue, File B → orange, both semi-transparent so they show through each other.
		this.stpFileAMeshes.forEach((m: any) => {
			m.material.color.setHex(this.stpFileAColour);
			m.material.transparent = true;
			m.material.opacity = this.stpCompareOpacity;
		});
		this.stpFileBMeshes.forEach((m: any) => {
			m.material.color.setHex(this.stpFileBColour);
			m.material.transparent = true;
			m.material.opacity = this.stpCompareOpacity;
		});
	}

	buildMeshesFromOcct(result: any, scene: any): OcctMeshBuildResult {
		const meshes: any[] = [];
		const edges: any[] = [];
		const parts: CadPart[] = [];

		for (let i = 0; i < result.meshes.length; i++) {
			const resultMesh = result.meshes[i];
			const positions = new Float32Array(resultMesh.attributes.position.array);
			const indices = new Uint32Array(resultMesh.index.array);
			const geometry = new THREE.BufferGeometry();
			geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
			if (resultMesh.attributes.normal) {
				geometry.setAttribute("normal", new THREE.Float32BufferAttribute(new Float32Array(resultMesh.attributes.normal.array), 3));
			}
			geometry.setIndex(new THREE.BufferAttribute(indices, 1));

			let surfaceArea = 0;
			let signedVolume = 0;
			for (let t = 0; t < indices.length; t += 3) {
				const i0 = indices[t] * 3; const i1 = indices[t + 1] * 3; const i2 = indices[t + 2] * 3;
				const ax = positions[i0]; const ay = positions[i0 + 1]; const az = positions[i0 + 2];
				const bx = positions[i1]; const by = positions[i1 + 1]; const bz = positions[i1 + 2];
				const cx = positions[i2]; const cy = positions[i2 + 1]; const cz = positions[i2 + 2];
				const crossX = (by - ay) * (cz - az) - (bz - az) * (cy - ay);
				const crossY = (bz - az) * (cx - ax) - (bx - ax) * (cz - az);
				const crossZ = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
				surfaceArea += Math.sqrt(crossX * crossX + crossY * crossY + crossZ * crossZ) * 0.5;
				signedVolume += (ax * (by * cz - bz * cy) + bx * (cy * az - cz * ay) + cx * (ay * bz - az * by)) / 6.0;
			}

			let color = 0x8899aa;
			if (resultMesh.color) {
				color = new THREE.Color(resultMesh.color[0], resultMesh.color[1], resultMesh.color[2]).getHex();
			}
			const material = new THREE.MeshPhongMaterial({ color, side: THREE.DoubleSide });
			const mesh = new THREE.Mesh(geometry, material);
			scene.add(mesh);
			meshes.push(mesh);

			geometry.computeBoundingBox();
			parts.push({ mesh, name: resultMesh.name || `Part ${i + 1}`, surfaceAreaMm2: surfaceArea, visible: true, volumeMm3: Math.abs(signedVolume) });

			const edgeGeometry = new THREE.EdgesGeometry(geometry, 20);
			const edgeMaterial = new THREE.LineBasicMaterial({ color: 0x222222, opacity: 0.6, transparent: true });
			const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
			edgeLines.visible = false;
			scene.add(edgeLines);
			edges.push(edgeLines);
		}

		return { edges, meshes, parts };
	}

	clearCompare() {
		if (!this.stpCompareMode) return;
		this.stpFileBMeshes.forEach((m: any) => this.stpScene?.remove(m));
		this.stpFileBEdges.forEach((e: any) => this.stpScene?.remove(e));
		this.stpFileBMeshes = [];
		this.stpFileBEdges = [];
		this.stpFileBParts = [];
		this.stpCompareMode = false;
		this.stpFileAVisible = true;
		this.stpFileBVisible = true;
		// Restore file A colour mode (was overridden by compare colours).
		this.applyColourMode();
		this.recomputeStats();
	}

	@HostListener("window:keydown", ["$event"])
	handleKeydown(event: KeyboardEvent) {
		if (!this.visible) return;
		const target = event.target as HTMLElement | null;
		const tag = (target?.tagName || "").toLowerCase();
		if (tag === "input" || tag === "textarea" || tag === "select") return;
		if (event.key === "f" || event.key === "F") {
			this.stpFitToView();
			event.preventDefault();
		}
	}

	loadBuffer(buffer: ArrayBuffer) {
		if (!buffer) return;
		this.visible = true;
		// Defer to next tick so embedded callers can call this before the container ngIf renders.
		setTimeout(() => this.renderStpModel(buffer), 0);
	}

	loadCompareBuffer(buffer: ArrayBuffer) {
		if (!buffer || !this.stpScene) return;
		const loadOcct = (): Promise<any> => {
			return new Promise((resolve) => {
				if ((window as any).occtimportjs) {
					(window as any).occtimportjs().then(resolve);
					return;
				}
				const script = document.createElement("script");
				script.src = "https://cdn.jsdelivr.net/npm/occt-import-js@0.0.23/dist/occt-import-js.js";
				script.onload = () => (window as any).occtimportjs().then(resolve);
				document.head.appendChild(script);
			});
		};
		loadOcct().then((oc: any) => {
			const fileBuffer = new Uint8Array(buffer);
			const result = oc.ReadStepFile(fileBuffer, null);
			const built = this.buildMeshesFromOcct(result, this.stpScene);
			this.stpFileBMeshes = built.meshes;
			this.stpFileBEdges = built.edges;
			this.stpFileBParts = built.parts;
			this.stpCompareMode = true;
			this.applyCompareColours();
			this.recomputeStats();
		});
	}

	ngOnDestroy() {
		// Break the render loop and let it dispose the renderer.
		this.visible = false;
	}

	onMaterialChanged() {
		this.recomputeMass();
	}

	open(lookupId: string) {
		if (!lookupId) return;
		this.visible = true;
		this.fileService.downloadFile(lookupId as any)
			.subscribe(
				(response: any) => {
					const reader = new FileReader();
					reader.onload = () => {
						const buffer = reader.result as ArrayBuffer;
						this.renderStpModel(buffer);
					};
					reader.readAsArrayBuffer(response);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Failed to load CAD file");
					this.visible = false;
				}
			);
	}

	recomputeMass() {
		const material = this.materials.find((m) => m.name === this.selectedMaterialName);
		if (!material || this.stpVolumeMm3 <= 0) { this.stpMass = ""; return; }
		// mass(g) = volume(mm³) × density(g/cm³) × (1 cm³ / 1000 mm³)
		const grams = this.stpVolumeMm3 * material.density / 1000;
		if (grams >= 1000) {
			this.stpMass = `${(grams / 1000).toFixed(2)} kg`;
		} else {
			this.stpMass = `${grams.toFixed(1)} g`;
		}
	}

	recomputeStats() {
		// Sum across visible file-A parts only — file B is treated as a compare overlay.
		let area = 0;
		let volume = 0;
		this.stpParts.forEach((p) => { if (p.visible) { area += p.surfaceAreaMm2; volume += p.volumeMm3; } });
		this.stpSurfaceArea = `${area.toFixed(1)} mm²`;
		this.stpVolumeMm3 = volume;
		this.stpVolume = `${volume.toFixed(1)} mm³`;
		this.recomputeMass();
	}

	renderGizmo() {
		if (!this.stpGizmoRenderer || !this.stpGizmoCamera || !this.stpGizmoScene || !this.stpCamera) return;
		// Mirror the main camera's view direction at a fixed distance from origin.
		const dir = new THREE.Vector3();
		this.stpCamera.getWorldDirection(dir);
		this.stpGizmoCamera.position.copy(dir.clone().multiplyScalar(-4));
		this.stpGizmoCamera.up.copy(this.stpCamera.up);
		this.stpGizmoCamera.lookAt(0, 0, 0);
		this.stpGizmoRenderer.render(this.stpGizmoScene, this.stpGizmoCamera);
	}

	renderStpModel(buffer: ArrayBuffer) {
		const container = this.viewerContainer?.nativeElement;
		if (!container) return;
		container.innerHTML = "<div style='display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af;'>Loading 3D model...</div>";

		const loadOcct = (): Promise<any> => {
			return new Promise((resolve) => {
				if ((window as any).occtimportjs) {
					(window as any).occtimportjs().then(resolve);
					return;
				}
				const script = document.createElement("script");
				script.src = "https://cdn.jsdelivr.net/npm/occt-import-js@0.0.23/dist/occt-import-js.js";
				script.onload = () => (window as any).occtimportjs().then(resolve);
				document.head.appendChild(script);
			});
		};

		loadOcct().then((oc: any) => {
			container.innerHTML = "";
			const fileBuffer = new Uint8Array(buffer);
			const result = oc.ReadStepFile(fileBuffer, null);

			const scene = new THREE.Scene();
			scene.background = new THREE.Color(0xf3f4f6);
			this.stpScene = scene;
			this.stpMeshes = [];
			this.stpFileAMeshes = [];
			this.stpFileBMeshes = [];
			this.stpFileBEdges = [];
			this.stpFileBParts = [];
			this.stpOriginalColours = [];
			this.stpMeshCenters = [];
			this.stpEdgeLines = [];
			this.stpParts = [];
			this.stpWireframe = false;
			this.stpColourMode = 0;
			this.stpBackgroundIndex = 0;
			this.stpExploded = false;
			this.stpEdgeOverlay = false;
			this.stpMeasureType = "";
			this.stpMeasurePoint1 = null;
			this.stpMeasureFirstNormal = null;
			this.stpMeasureMarkers = [];
			this.stpMeasureResult = "";
			this.stpBoundingBoxHelper = null;
			this.stpBoundingBoxVisible = false;
			this.stpIsOrthographic = false;
			this.stpAutoRotate = false;
			this.stpSectionVisible = false;
			this.stpClippingPlane = null;
			this.stpCompareMode = false;
			this.stpFileAVisible = true;
			this.stpFileBVisible = true;

			scene.add(new THREE.AmbientLight(0xffffff, 0.6));
			const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
			dirLight.position.set(1, 1, 1);
			scene.add(dirLight);
			const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
			dirLight2.position.set(-1, -1, -0.5);
			scene.add(dirLight2);

			const built = this.buildMeshesFromOcct(result, scene);
			this.stpMeshes = built.meshes.slice();
			this.stpFileAMeshes = built.meshes.slice();
			this.stpParts = built.parts;
			this.stpEdgeLines = built.edges;
			this.stpMeshCenters = built.parts.map((p) => {
				const geometry = p.mesh.geometry;
				return geometry.boundingBox ? geometry.boundingBox.getCenter(new THREE.Vector3()) : new THREE.Vector3();
			});
			this.stpOriginalColours = built.meshes.map((m: any) => m.material.color.getHex());

			// Default to grey on load.
			this.applyColourMode();
			this.recomputeStats();

			const box = new THREE.Box3().setFromObject(scene);
			const center = box.getCenter(new THREE.Vector3());
			const dims = box.getSize(new THREE.Vector3());
			const size = dims.length();
			this.stpDimensions = `${dims.x.toFixed(1)} x ${dims.y.toFixed(1)} x ${dims.z.toFixed(1)} mm`;
			this.stpCenter = center.clone();
			this.stpDims = dims.clone();
			this.stpSize = size;

			const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, size / 1000, size * 10);
			camera.position.copy(center).add(new THREE.Vector3(size * 0.8, size * 0.6, size * 0.8));
			camera.lookAt(center);
			this.stpCamera = camera;
			this.stpInitialCameraPos = camera.position.clone();
			this.stpInitialTarget = center.clone();

			const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
			renderer.setSize(container.clientWidth, container.clientHeight);
			renderer.setPixelRatio(window.devicePixelRatio);
			renderer.localClippingEnabled = true;
			container.appendChild(renderer.domElement);
			this.stpRenderer = renderer;

			const controls = new OrbitControls(camera, renderer.domElement);
			controls.target.copy(center);
			controls.autoRotateSpeed = 1.5;
			controls.update();
			this.stpControls = controls;

			// Corner XYZ gizmo (small overlay)
			this.setupGizmo(container);

			const raycaster = new THREE.Raycaster();
			const mouse = new THREE.Vector2();
			renderer.domElement.addEventListener("click", (event: MouseEvent) => {
				if (!this.stpMeasureType) return;
				const rect = renderer.domElement.getBoundingClientRect();
				mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
				mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
				raycaster.setFromCamera(mouse, this.stpCamera);
				const intersects = raycaster.intersectObjects(this.stpMeshes);
				if (intersects.length === 0) return;
				const hit = intersects[0];
				const point = hit.point;
				const marker = new THREE.Mesh(
					new THREE.SphereGeometry(size * 0.008, 16, 16),
					new THREE.MeshBasicMaterial({ color: 0xff0000 })
				);
				marker.position.copy(point);
				scene.add(marker);
				this.stpMeasureMarkers.push(marker);

				if (this.stpMeasureType === "distance") {
					if (!this.stpMeasurePoint1) {
						this.stpMeasurePoint1 = point.clone();
						this.stpMeasureResult = "Click second point…";
					} else {
						const dist = this.stpMeasurePoint1.distanceTo(point);
						this.stpMeasureResult = `Distance: ${dist.toFixed(2)} mm`;
						this.stpMeasurePoint1 = null;
						this.scheduleMeasureMarkerCleanup();
					}
				} else if (this.stpMeasureType === "angle") {
					const normal = hit.face?.normal?.clone();
					if (!normal) return;
					normal.transformDirection(hit.object.matrixWorld).normalize();
					if (!this.stpMeasureFirstNormal) {
						this.stpMeasureFirstNormal = normal;
						this.stpMeasureResult = "Click second face…";
					} else {
						const cos = Math.max(-1, Math.min(1, this.stpMeasureFirstNormal.dot(normal)));
						const angleDeg = (Math.acos(cos) * 180) / Math.PI;
						this.stpMeasureResult = `Angle: ${angleDeg.toFixed(2)}°`;
						this.stpMeasureFirstNormal = null;
						this.scheduleMeasureMarkerCleanup();
					}
				}
			});

			const animate = () => {
				if (!this.visible) {
					renderer.dispose();
					if (this.stpGizmoRenderer) this.stpGizmoRenderer.dispose();
					return;
				}
				requestAnimationFrame(animate);
				controls.update();
				renderer.render(scene, this.stpCamera);
				this.renderGizmo();
			};
			animate();
		});
	}

	requestCompareFile() {
		this.compareFileRequested.emit();
	}

	scheduleMeasureMarkerCleanup() {
		const markers = this.stpMeasureMarkers.slice();
		this.stpMeasureMarkers = [];
		setTimeout(() => { markers.forEach((m) => this.stpScene?.remove(m)); }, 5000);
	}

	setupGizmo(container: HTMLDivElement) {
		// Tear down any existing gizmo (e.g. when loading a new file).
		if (this.stpGizmoContainer && this.stpGizmoContainer.parentElement) {
			this.stpGizmoContainer.parentElement.removeChild(this.stpGizmoContainer);
		}
		const gizmoContainer = document.createElement("div");
		gizmoContainer.className = "cad-gizmo";
		container.appendChild(gizmoContainer);
		this.stpGizmoContainer = gizmoContainer;

		const gizmoScene = new THREE.Scene();
		const gizmoCamera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
		gizmoCamera.position.set(0, 0, 5);

		const axes = new THREE.AxesHelper(1.4);
		gizmoScene.add(axes);

		const gizmoRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
		gizmoRenderer.setSize(80, 80);
		gizmoRenderer.setClearColor(0x000000, 0);
		gizmoContainer.appendChild(gizmoRenderer.domElement);

		this.stpGizmoScene = gizmoScene;
		this.stpGizmoCamera = gizmoCamera;
		this.stpGizmoRenderer = gizmoRenderer;
	}

	stpClearMeasurement() {
		this.stpMeasureType = "";
		this.stpMeasurePoint1 = null;
		this.stpMeasureFirstNormal = null;
		this.stpMeasureResult = "";
		if (this.stpControls) this.stpControls.enabled = true;
		// Clear any sitting markers immediately
		this.stpMeasureMarkers.forEach((m) => this.stpScene?.remove(m));
		this.stpMeasureMarkers = [];
	}

	stpCycleBackground() {
		const backgrounds = [0xf3f4f6, 0xffffff, 0x1f2937, 0x000000];
		this.stpBackgroundIndex = (this.stpBackgroundIndex + 1) % backgrounds.length;
		if (this.stpScene) this.stpScene.background = new THREE.Color(backgrounds[this.stpBackgroundIndex]);
	}

	stpCycleColourMode() {
		if (this.stpCompareMode) return;
		this.stpColourMode = (this.stpColourMode + 1) % 4;
		this.applyColourMode();
	}

	stpFitToView() {
		if (!this.stpCamera || !this.stpControls || !this.stpScene) return;
		const visibleMeshes = [...this.stpFileAMeshes.filter((m: any) => m.visible), ...this.stpFileBMeshes.filter((m: any) => m.visible)];
		if (visibleMeshes.length === 0) return;
		const box = new THREE.Box3();
		visibleMeshes.forEach((m: any) => box.expandByObject(m));
		const center = box.getCenter(new THREE.Vector3());
		const dims = box.getSize(new THREE.Vector3());
		const size = dims.length();
		if (this.stpIsOrthographic) {
			const container = this.viewerContainer?.nativeElement;
			if (!container) return;
			const aspect = container.clientWidth / container.clientHeight;
			const frustum = size * 1.1;
			this.stpCamera.left = -frustum * aspect / 2;
			this.stpCamera.right = frustum * aspect / 2;
			this.stpCamera.top = frustum / 2;
			this.stpCamera.bottom = -frustum / 2;
			this.stpCamera.zoom = 1;
			this.stpCamera.updateProjectionMatrix();
			const offset = new THREE.Vector3().subVectors(this.stpCamera.position, this.stpControls.target).normalize().multiplyScalar(size * 1.5);
			this.stpCamera.position.copy(center).add(offset);
		} else {
			const fov = this.stpCamera.fov * (Math.PI / 180);
			const distance = (size * 0.6) / Math.tan(fov / 2);
			const dir = new THREE.Vector3().subVectors(this.stpCamera.position, this.stpControls.target).normalize();
			this.stpCamera.position.copy(center).add(dir.multiplyScalar(distance));
		}
		this.stpControls.target.copy(center);
		this.stpControls.update();
	}

	stpInvertSection() {
		this.stpSectionInverted = !this.stpSectionInverted;
		this.stpUpdateSection();
	}

	stpIsolatePart(part: CadPart) {
		this.stpParts.forEach((p) => { p.visible = p === part; p.mesh.visible = p.visible; });
		this.recomputeStats();
	}

	stpResetCamera() {
		if (this.stpCamera && this.stpControls && this.stpInitialCameraPos && this.stpInitialTarget) {
			this.stpCamera.position.copy(this.stpInitialCameraPos);
			this.stpControls.target.copy(this.stpInitialTarget);
			this.stpControls.update();
		}
	}

	stpScreenshot() {
		if (!this.stpRenderer) return;
		const link = document.createElement("a");
		link.download = `${this.screenshotName}.png`;
		link.href = this.stpRenderer.domElement.toDataURL("image/png");
		link.click();
	}

	stpSetMeasureType(type: CadMeasureType) {
		// If already on the requested type, clicking again turns it off.
		if (this.stpMeasureType === type) { this.stpClearMeasurement(); return; }
		this.stpMeasureType = type;
		this.stpMeasurePoint1 = null;
		this.stpMeasureFirstNormal = null;
		this.stpMeasureResult = type === "distance" ? "Click first point…" : type === "angle" ? "Click first face…" : "";
		if (this.stpControls) this.stpControls.enabled = !type;
	}

	stpSetSectionAxis(axis: CadSectionAxis) {
		this.stpSectionAxis = axis;
		this.stpUpdateSection();
	}

	stpSetView(view: CadStandardView) {
		if (!this.stpCamera || !this.stpControls || !this.stpCenter || this.stpSize <= 0) return;
		const c = this.stpCenter as THREE.Vector3;
		const d = this.stpSize * 1.4;
		const pos = c.clone();
		const up = new THREE.Vector3(0, 1, 0);
		switch (view) {
			case "front": pos.add(new THREE.Vector3(0, 0, d)); break;
			case "back": pos.add(new THREE.Vector3(0, 0, -d)); break;
			case "top": pos.add(new THREE.Vector3(0, d, 0)); up.set(0, 0, -1); break;
			case "bottom": pos.add(new THREE.Vector3(0, -d, 0)); up.set(0, 0, 1); break;
			case "left": pos.add(new THREE.Vector3(-d, 0, 0)); break;
			case "right": pos.add(new THREE.Vector3(d, 0, 0)); break;
			case "iso": pos.add(new THREE.Vector3(d * 0.6, d * 0.45, d * 0.6)); break;
		}
		this.stpCamera.up.copy(up);
		this.stpCamera.position.copy(pos);
		this.stpControls.target.copy(c);
		this.stpControls.update();
	}

	stpShowAllParts() {
		this.stpParts.forEach((p) => { p.visible = true; p.mesh.visible = true; });
		this.recomputeStats();
	}

	stpToggleAutoRotate() {
		if (!this.stpControls) return;
		this.stpAutoRotate = !this.stpAutoRotate;
		this.stpControls.autoRotate = this.stpAutoRotate;
	}

	stpToggleBoundingBox() {
		if (!this.stpScene) return;
		this.stpBoundingBoxVisible = !this.stpBoundingBoxVisible;
		if (this.stpBoundingBoxVisible) {
			const box = new THREE.Box3().setFromObject(this.stpScene);
			this.stpBoundingBoxHelper = new THREE.Box3Helper(box, new THREE.Color(0xff6b00));
			this.stpScene.add(this.stpBoundingBoxHelper);
		} else if (this.stpBoundingBoxHelper) {
			this.stpScene.remove(this.stpBoundingBoxHelper);
			this.stpBoundingBoxHelper = null;
		}
	}

	stpToggleEdgeOverlay() {
		this.stpEdgeOverlay = !this.stpEdgeOverlay;
		[...this.stpEdgeLines, ...this.stpFileBEdges].forEach((line: any) => { line.visible = this.stpEdgeOverlay; });
	}

	stpToggleExploded() {
		this.stpExploded = !this.stpExploded;
		if (this.stpMeshes.length <= 1) return;
		const sceneBox = new THREE.Box3().setFromObject(this.stpScene);
		const sceneCenter = sceneBox.getCenter(new THREE.Vector3());
		const explodeScale = sceneBox.getSize(new THREE.Vector3()).length() * 0.3;

		this.stpMeshes.forEach((mesh: any, i: number) => {
			if (this.stpExploded) {
				const meshCenter = this.stpMeshCenters[i];
				const direction = new THREE.Vector3().subVectors(meshCenter, sceneCenter).normalize();
				mesh.position.copy(direction.multiplyScalar(explodeScale));
			} else {
				mesh.position.set(0, 0, 0);
			}
		});
	}

	stpToggleFileAVisible() {
		this.stpFileAVisible = !this.stpFileAVisible;
		this.stpFileAMeshes.forEach((m: any) => { m.visible = this.stpFileAVisible; });
	}

	stpToggleFileBVisible() {
		this.stpFileBVisible = !this.stpFileBVisible;
		this.stpFileBMeshes.forEach((m: any) => { m.visible = this.stpFileBVisible; });
	}

	stpToggleOrthographic() {
		if (!this.stpCamera || !this.stpControls || !this.stpRenderer || this.stpSize <= 0) return;
		const container = this.viewerContainer?.nativeElement;
		if (!container) return;
		const aspect = container.clientWidth / container.clientHeight;
		const oldPos = this.stpCamera.position.clone();
		const oldTarget = this.stpControls.target.clone();
		const oldUp = this.stpCamera.up.clone();
		let newCamera: any;
		if (this.stpIsOrthographic) {
			newCamera = new THREE.PerspectiveCamera(45, aspect, this.stpSize / 1000, this.stpSize * 10);
		} else {
			const frustum = this.stpSize * 0.8;
			newCamera = new THREE.OrthographicCamera(
				-frustum * aspect / 2,
				frustum * aspect / 2,
				frustum / 2,
				-frustum / 2,
				this.stpSize / 1000,
				this.stpSize * 10
			);
		}
		newCamera.position.copy(oldPos);
		newCamera.up.copy(oldUp);
		newCamera.lookAt(oldTarget);
		newCamera.updateProjectionMatrix();
		this.stpCamera = newCamera;
		this.stpControls.object = newCamera;
		this.stpControls.update();
		this.stpIsOrthographic = !this.stpIsOrthographic;
	}

	stpTogglePartTree() {
		this.stpPartTreeVisible = !this.stpPartTreeVisible;
	}

	stpTogglePartVisible(part: CadPart) {
		part.visible = !part.visible;
		part.mesh.visible = part.visible;
		this.recomputeStats();
	}

	stpToggleSection() {
		this.stpSectionVisible = !this.stpSectionVisible;
		if (this.stpSectionVisible) {
			this.stpUpdateSection();
		} else {
			this.stpMeshes.forEach((m: any) => { m.material.clippingPlanes = []; m.material.clipShadows = false; });
			this.stpFileBMeshes.forEach((m: any) => { m.material.clippingPlanes = []; m.material.clipShadows = false; });
			this.stpClippingPlane = null;
		}
	}

	stpToggleWireframe() {
		this.stpWireframe = !this.stpWireframe;
		[...this.stpMeshes, ...this.stpFileBMeshes].forEach((mesh: any) => { mesh.material.wireframe = this.stpWireframe; });
	}

	stpUpdateCompareOpacity() {
		const opacity = Math.min(1, Math.max(0, this.stpCompareOpacity));
		[...this.stpFileAMeshes, ...this.stpFileBMeshes].forEach((m: any) => { m.material.opacity = opacity; m.material.transparent = opacity < 1; });
	}

	stpUpdateSection() {
		if (!this.stpSectionVisible || !this.stpScene || !this.stpDims || !this.stpCenter) return;
		const box = new THREE.Box3().setFromObject(this.stpScene);
		const min = box.min;
		const max = box.max;
		const t = Math.min(100, Math.max(0, this.stpSectionPosition)) / 100;
		let normal: THREE.Vector3;
		let constant: number;
		// THREE.Plane clips out points where normal·p + constant > 0.
		if (this.stpSectionAxis === "x") {
			const x = min.x + t * (max.x - min.x);
			normal = new THREE.Vector3(this.stpSectionInverted ? -1 : 1, 0, 0);
			constant = this.stpSectionInverted ? x : -x;
		} else if (this.stpSectionAxis === "y") {
			const y = min.y + t * (max.y - min.y);
			normal = new THREE.Vector3(0, this.stpSectionInverted ? -1 : 1, 0);
			constant = this.stpSectionInverted ? y : -y;
		} else {
			const z = min.z + t * (max.z - min.z);
			normal = new THREE.Vector3(0, 0, this.stpSectionInverted ? -1 : 1);
			constant = this.stpSectionInverted ? z : -z;
		}
		if (this.stpClippingPlane) {
			this.stpClippingPlane.normal.copy(normal);
			this.stpClippingPlane.constant = constant;
		} else {
			this.stpClippingPlane = new THREE.Plane(normal, constant);
		}
		const planes = [this.stpClippingPlane];
		[...this.stpMeshes, ...this.stpFileBMeshes].forEach((m: any) => { m.material.clippingPlanes = planes; m.material.clipShadows = true; });
	}
}
